import React, {
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { IZoomContext } from './interfaces';
import ZoomVideo, {
  CaptureVideoOption,
  ExceptionCode,
  LocalAudioTrack,
  MediaDevice,
  Participant,
  Stream,
  VideoClient,
} from '@zoom/videosdk';
import useAsyncEffect from 'use-async-effect';
import { useMytaverseEvent } from '../../../providers';
import ZoomService from '../../../../../services/ZoomService';
import { useConference } from '../Dolby';
import { useConferenceState } from '../../../../../hooks/conferenceContext';
import { useEventContext } from '../../../../../ek/providers/EventProvider';
import {
  getErrorNotification,
  useNotification,
} from '../../../../../components/Notification';
import { isZoomError, withTimeout } from './helpers';
import {
  NavigatorPermissionsService,
  PermissionName,
} from '../../../../../helpers/permissionsService';
import { useMytaverse } from '../../../../../providers/MytaverseProvider';
import { default as zoomProviderLogger } from './logger';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const ZoomContext = React.createContext<IZoomContext>({});

export const useZoom = () => useContext(ZoomContext);

type ZoomProviderProps = {
  zmClient: typeof VideoClient;
  children: ReactNode;
};

export const ZoomProvider: React.FC<ZoomProviderProps> = ({
  zmClient,
  children,
}) => {
  const {
    currentParticipant,
    currentRegion,
    currentRoom,
    muted,
    setMuted,
    currentEvent,
    isGamecastCrash,
  } = useMytaverseEvent();

  const { websocketSessionId } = useMytaverse();
  const { isEventRun } = useEventContext();

  const {
    ueWebcamScreenName,
    setVideoStarted,
    ueScreenScreenName,
    ueScreenMediaStreams,
  } = useConference();

  const { t: translate } = useTranslation('common');
  const { showNotification, getSuccessNotification } = useNotification();

  const [loading, setIsLoading] = useState(true);
  const [mediaStream, setMediaStream] = useState<typeof Stream | null>(null);
  const [isSupportGalleryView, setIsSupportGalleryView] =
    useState<boolean>(false);
  const [localAudioTrack, setLocalAudioTrack] =
    useState<LocalAudioTrack | null>(null);
  const [zoomToken, setZoomToken] = useState<string | null>('');
  const [zoomUE5Token, setZoomUE5Token] = useState<string | null>('');
  const [isInConference, setIsInConference] = useState(false);
  const [micList, setMicList] = useState<MediaDevice[]>(
    mediaStream?.getMicList() ?? [],
  );
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>(
    mediaStream?.getSpeakerList() ?? [],
  );
  const [cameraList, setCameraList] = useState<MediaDevice[]>(
    mediaStream?.getCameraList() ?? [],
  );
  const [isCurrentParticipantSpeaking, setIsCurrentParticipantSpeaking] =
    useState(false);
  const [isLimitedScreenResolution, setIsLimitedScreenResolution] =
    useState(false);
  const [zoomConferenceName, setZoomConferenceName] = useState<string | null>(
    '',
  );
  const [zoomUser, setZoomUser] = useState<Participant | null>(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isSharingToScreen, setIsSharingToScreen] = useState(false);
  const [isSharingScreenToUE, setIsSharingScreenToUE] = useState(false);
  const [expandedScreenSharing, setExpandedScreenSharing] = useState(false);
  const [expandedCameraSharing, setExpandedCameraSharing] = useState(false);

  const shareViewRef = useRef<{
    selfShareRef: HTMLCanvasElement | HTMLVideoElement | null;
  }>(null);

  const [isJoiningConference, setIsJoiningConference] = useState(false);
  const internalIsJoiningConference = useRef(false);
  const [forceRetryJoining, setForceRetryJoining] = useState(false);
  const [permissionsState, setPermissionsState] = useState({
    microphone: 'denied',
    camera: 'denied',
  });

  const { activeSpeakerDeviceId, activeMicroDeviceId, activeCameraDeviceId } =
    useConferenceState();

  const handleZoomError = (error: any) => {
    if (isZoomError(error)) {
      switch (error.errorCode) {
        case ExceptionCode.VIDEO_CAMERA_PERMISSION_DENIED:
          showNotification(
            getErrorNotification({
              message: translate(
                'notifications.video.start.userForbiddenCapture',
              ),
            }),
          );
          break;
        case ExceptionCode.VIDEO_CAMERA_TAKEN:
          showNotification(
            getErrorNotification({
              message: translate('notifications.video.start.cameraIsTaken'),
            }),
          );
          break;
      }
    }
  };

  useAsyncEffect(async () => {
    if (!zmClient) {
      return;
    }
    zoomProviderLogger.log('Initializing Zoom client');

    try {
      setIsLoading(true);
      checkPermissions();
      await zmClient.init('en-US', 'Global', { patchJsMedia: true });

      const stream = zmClient.getMediaStream();
      const localAudio = ZoomVideo.createLocalAudioTrack();

      setLocalAudioTrack(localAudio);
      setMediaStream(stream);
      setIsSupportGalleryView(stream.isSupportMultipleVideos());
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      zoomProviderLogger.log(e.reason);
    }

    return () => {
      zoomProviderLogger.log('Cleaning up Zoom client');
      ZoomVideo.destroyClient();
    };
  }, [zmClient]);

  useAsyncEffect(async () => {
    if (!mediaStream || !mediaStream?.getCameraList().length) {
      return;
    }

    if (
      mediaStream &&
      mediaStream.getCameraList().length &&
      mediaStream.getActiveCamera() !== activeCameraDeviceId
    ) {
      try {
        await mediaStream.switchCamera(activeCameraDeviceId);
      } catch (error) {
        zoomProviderLogger.log(`Switching camera error: ${error}`);
      }
    }
  }, [mediaStream, activeCameraDeviceId]);

  useAsyncEffect(async () => {
    if (
      mediaStream &&
      mediaStream.getSpeakerList().length &&
      mediaStream.getActiveSpeaker() !== activeSpeakerDeviceId
    ) {
      try {
        await mediaStream.switchSpeaker(activeSpeakerDeviceId);
      } catch (error) {
        zoomProviderLogger.log(`Switching speaker error: ${error}`);
      }
    }
  }, [mediaStream, activeSpeakerDeviceId]);

  useAsyncEffect(async () => {
    if (
      mediaStream &&
      mediaStream.getMicList().length &&
      mediaStream.getActiveMicrophone() !== activeMicroDeviceId
    ) {
      try {
        await mediaStream.switchMicrophone(activeMicroDeviceId);
      } catch (error) {
        zoomProviderLogger.log(`Switching microphone error: ${error}`);
      }
    }
  }, [mediaStream, activeMicroDeviceId]);

  const onActiveSpeakerChange = useCallback(
    (payload: any) => {
      if (Array.isArray(payload) && payload.length > 0) {
        const isCurrentUserSpeaking = payload.some(
          (item: { userId: number; displayName: string }) =>
            item.userId === zmClient.getSessionInfo().userId,
        );

        setIsCurrentParticipantSpeaking(isCurrentUserSpeaking);

        return;
      }

      setIsCurrentParticipantSpeaking(false);
    },
    [zmClient],
  );

  const stopShareCameraToScreen = useCallback(async () => {
    zoomProviderLogger.log('stopShareCameraToScreen called');
    if (!mediaStream || !currentParticipant) {
      return;
    }

    const mediaStreamId = mediaStream.getActiveVideoId();

    if (mediaStreamId) {
      await Promise.all([
        mediaStream.stopVideo(),
        ZoomService.deleteZoomMediaStream({
          mediaStreamId: `${currentParticipant.participantId}-camera`,
        }),
      ]);
      setIsSharingToScreen(false);
      setVideoStarted(false);
    }
  }, [mediaStream, currentParticipant]);

  const startShareCameraToScreen = useCallback(async () => {
    zoomProviderLogger.log('startShareCameraToScreen called');
    try {
      const currentZoomUser = zmClient.getCurrentUserInfo();

      if (
        !currentZoomUser.bVideoShare &&
        mediaStream &&
        currentEvent &&
        currentRoom &&
        currentParticipant &&
        ueWebcamScreenName &&
        zoomConferenceName
      ) {
        await mediaStream.startVideo({ cameraId: activeCameraDeviceId });

        await ZoomService.createZoomMediaStream({
          mediaStreamId: `${currentParticipant.participantId}-camera`,
          ueScreenName: ueWebcamScreenName,
          owner: {
            id: currentParticipant?.participantId,
            name: currentParticipant?.name,
          },
          source: 'CAMERA',
          muted: true,
          eventId: currentEvent.eventId,
          roomId: currentRoom.roomId,
          participantId: currentParticipant.participantId,
          region: currentRegion ? currentRegion.region : '',
          zoomUserId: currentZoomUser.userId,
          conferenceId: zoomConferenceName,
          websocketSessionId,
        });

        setIsSharingToScreen(true);
        setVideoStarted(true);
      }
    } catch (error: unknown) {
      setVideoStarted(false);
      setIsSharingToScreen(false);
      handleZoomError(error);
    }
  }, [
    activeCameraDeviceId,
    mediaStream,
    currentRoom,
    currentRegion,
    currentEvent,
    ueWebcamScreenName,
    zoomConferenceName,
    currentParticipant,
    zmClient,
    websocketSessionId,
  ]);

  const startScreenSharing = useCallback(async () => {
    try {
      zoomProviderLogger.log('startScreenSharing called');
      if (
        !mediaStream ||
        !ueScreenScreenName ||
        !currentParticipant ||
        !currentEvent ||
        !currentRoom ||
        !zoomConferenceName
      ) {
        return;
      }
      const currentZoomUser = zmClient.getCurrentUserInfo();

      if (mediaStream.isStartShareScreenWithVideoElement()) {
        const video = document.getElementById(
          'screen-share-content-video',
        ) as HTMLVideoElement | null;

        if (!video) {
          return;
        }

        await mediaStream.startShareScreen(video);

        //reformat in case of preview
        // video.style.display = 'none';
      } else {
        const canvas = document.getElementById(
          'screen-share-content-canvas',
        ) as HTMLCanvasElement | null;

        if (!canvas) {
          return;
        }

        await mediaStream.startShareScreen(
          canvas,
          isLimitedScreenResolution
            ? {
                captureHeight: 1080,
                captureWidth: 1920,
                optimizedForSharedVideo: true,
              }
            : undefined,
        );

        canvas.style.display = 'none';
      }

      await ZoomService.createZoomMediaStream({
        mediaStreamId: `${currentParticipant.participantId}-screen`,
        ueScreenName: ueScreenScreenName,
        owner: {
          id: currentParticipant?.participantId,
          name: currentParticipant?.name,
        },
        source: 'SCREEN',
        muted: true,
        eventId: currentEvent.eventId,
        roomId: currentRoom.roomId,
        participantId: currentParticipant.participantId,
        region: currentRegion ? currentRegion.region : '',
        zoomUserId: currentZoomUser.userId,
        conferenceId: zoomConferenceName,
        websocketSessionId,
      });

      setIsSharingScreenToUE(true);
    } catch (error) {
      if (isZoomError(error)) {
        if (error.errorCode === ExceptionCode.SCREEN_SHARE_USER_DENIED) {
          // we ignore this error because user just clicked Cancel button on the screen share dialog
          zoomProviderLogger.log('Screen share user denied');
          return;
        }
      }

      throw error;
    }
  }, [
    mediaStream,
    shareViewRef,
    ueScreenScreenName,
    currentParticipant,
    currentEvent,
    zmClient,
    currentRoom,
    currentRegion,
    zoomConferenceName,
    isLimitedScreenResolution,
    websocketSessionId,
  ]);

  const stopScreenSharing = useCallback(async () => {
    zoomProviderLogger.log('stopScreenSharing called');
    if (!currentParticipant) {
      return;
    }

    if (!mediaStream || !zmClient.getSessionInfo()?.isInMeeting) {
      return;
    }

    try {
      await mediaStream?.stopShareScreen();
      await ZoomService.deleteZoomMediaStream({
        mediaStreamId: `${currentParticipant.participantId}-screen`,
      });
    } catch (error) {
      zoomProviderLogger.log(`Error in the stopScreenSharing: ${error}`);
    }

    setIsSharingScreenToUE(false);
  }, [mediaStream, ueScreenScreenName, zmClient]);

  const handlePermissionChange = useCallback(async () => {
    if (mediaStream) {
      const microphonePermissionState = await checkSinglePermission(
        'microphone',
      );
      const cameraPermissionState = await checkSinglePermission('camera');

      setPermissionsState({
        microphone: microphonePermissionState,
        camera: cameraPermissionState,
      });

      if (!internalIsJoiningConference.current) {
        setForceRetryJoining(true);
      }
    }
  }, [mediaStream]);

  const handlePassivelyStopShare = useCallback(async () => {
    await stopScreenSharing();
  }, [stopScreenSharing]);

  useEffect(() => {
    zmClient.on('passively-stop-share', handlePassivelyStopShare);
    zmClient.on('device-permission-change', handlePermissionChange);
    zmClient.on('active-speaker', onActiveSpeakerChange);

    return () => {
      zmClient.off('passively-stop-share', handlePassivelyStopShare);
      zmClient.off('device-permission-change', handlePermissionChange);
      zmClient.off('active-speaker', onActiveSpeakerChange);
    };
  }, [zmClient, handlePassivelyStopShare, onActiveSpeakerChange]);

  const alignPermissionsWithListDevices = useCallback(() => {
    if (permissionsState.camera === 'granted' && cameraList.length === 0) {
      const listCameras = mediaStream?.getCameraList();
      if (listCameras?.length && listCameras[0].deviceId) {
        setCameraList(listCameras);
      }
    }

    if (permissionsState.camera === 'denied' && cameraList.length) {
      setCameraList([]);
    }

    if (permissionsState.microphone === 'granted' && speakerList.length === 0) {
      const listSpeakers = mediaStream?.getSpeakerList();
      if (listSpeakers?.length && listSpeakers[0].deviceId) {
        setSpeakerList(listSpeakers);
      }
    }

    if (permissionsState.microphone === 'denied' && speakerList.length) {
      setSpeakerList([]);
    }

    if (permissionsState.microphone === 'granted' && micList.length === 0) {
      const listMics = mediaStream?.getMicList();
      if (listMics?.length && listMics[0].deviceId) {
        setMicList(listMics);
      }
    }

    if (permissionsState.microphone === 'denied' && micList.length) {
      setMicList([]);
    }
  }, [permissionsState, cameraList, micList, speakerList, mediaStream]);

  const handleJoinToConference = useCallback(
    async (eventId: string, roomId: string, regionName?: string) => {
      const sessionName = regionName
        ? `${eventId}-${roomId}-${regionName}`
        : `${eventId}-${roomId}`;

      let webToken, ue5Token, currentZoomUser, currentSession;

      alignPermissionsWithListDevices();

      try {
        const currentSessionFromZoom = zmClient.getSessionInfo()?.topic;
        currentSession = currentSessionFromZoom;

        if (
          !mediaStream ||
          !currentParticipant ||
          (!forceRetryJoining && sessionName === currentSession)
        ) {
          return;
        }

        setIsJoiningConference(true);
        internalIsJoiningConference.current = true;

        if (isInConference) {
          if (isSharingScreenToUE) {
            await stopScreenSharing();
          }

          if (isSharingToScreen) {
            await stopShareCameraToScreen();
          }
          await zmClient.leave();
          setIsInConference(false);
        }

        const [webTokenZoom, ue5TokenZoom] = await Promise.all([
          await ZoomService.generateZoomToken({
            role: 1,
            expirationSeconds: 10800,
            sessionName: sessionName,
            userIdentity: currentParticipant.streamChatMemberId,
          }),
          await ZoomService.generateZoomToken({
            role: 1,
            expirationSeconds: 10800,
            sessionName: sessionName,
            userIdentity: currentParticipant.streamChatMemberId,
          }),
        ]);
        currentZoomUser = zmClient.getCurrentUserInfo();
        webToken = webTokenZoom;
        ue5Token = ue5TokenZoom;
      } catch (error) {
        zoomProviderLogger.error(error);
        setIsInConference(false);
        setZoomConferenceName(null);
        setIsJoiningConference(false);
        internalIsJoiningConference.current = false;
        setForceRetryJoining(true);
        return;
      }

      try {
        await withTimeout(
          zmClient.join(
            sessionName,
            webToken.signature,
            currentParticipant.fullName ||
              currentParticipant.email ||
              currentParticipant.participantId,
          ),
          10000,
          'zmClient.join has timed out',
        );
      } catch (error) {
        zoomProviderLogger.error(error);
        setIsInConference(false);
        setZoomConferenceName(null);
        setIsJoiningConference(false);
        internalIsJoiningConference.current = false;
        if (!forceRetryJoining) {
          // first time erroring
          showNotification(
            getErrorNotification({
              message: translate('notifications.zoom.join.timeout'),
            }),
          );
        } else {
          // second or more time erroring
          showNotification(
            getErrorNotification({
              message: translate('notifications.zoom.join.failed'),
              withReloadButton: true,
            }),
          );
        }
        setForceRetryJoining(true);
        zmClient.leave();
        return;
      }

      try {
        if (
          isEventRun &&
          mediaStream &&
          zmClient.getSessionInfo()?.isInMeeting
        ) {
          await mediaStream.startAudio({
            highBitrate: true,
            mute: muted,
            speakerId: activeSpeakerDeviceId,
          });
        }
      } catch (error) {
        zoomProviderLogger.error('error with the microphone ', error);
      }

      try {
        if (isCameraOn) {
          await startVideoSharing();
        }
      } catch (error) {
        zoomProviderLogger.error('error with the camera ', error);
      }

      zoomProviderLogger.log(`Current Zoom Session: ${sessionName}`);

      setForceRetryJoining(false);
      setIsInConference(true);
      if (currentZoomUser) {
        setZoomUser(currentZoomUser);
      }
      setZoomConferenceName(sessionName);
      if (webToken) {
        setZoomToken(webToken.signature);
      }
      if (ue5Token) {
        setZoomUE5Token(ue5Token.signature);
      }
      setIsJoiningConference(false);
      internalIsJoiningConference.current = false;
    },
    [
      zmClient,
      localAudioTrack,
      mediaStream,
      currentParticipant,
      zoomConferenceName,
      isSharingScreenToUE,
      isSharingToScreen,
      isInConference,
      stopShareCameraToScreen,
      stopScreenSharing,
      isCameraOn,
      muted,
      activeSpeakerDeviceId,
      isEventRun,
      forceRetryJoining,
    ],
  );

  useEffect(() => {
    setZoomUser(zmClient.getCurrentUserInfo());
  }, [zmClient, mediaStream]);

  useAsyncEffect(() => {
    if (isGamecastCrash) {
      zoomProviderLogger.log('Handling gamecast crash');
      ZoomVideo.destroyClient();
    }
  }, [isGamecastCrash, ZoomVideo]);

  useAsyncEffect(async () => {
    if (
      !mediaStream ||
      !isEventRun ||
      !zmClient.getSessionInfo()?.isInMeeting
    ) {
      return;
    }
    zoomProviderLogger.log('Starting audio stream');

    await mediaStream.startAudio({
      highBitrate: true,
      mute: muted,
      speakerId: activeSpeakerDeviceId,
    });
    zoomProviderLogger.log('Successfully started audio stream');
  }, [mediaStream, activeSpeakerDeviceId, mediaStream, isEventRun]);

  useAsyncEffect(async () => {
    if (
      !mediaStream ||
      !currentRoom ||
      !zmClient.getSessionInfo()?.isInMeeting
    ) {
      return;
    }

    try {
      if (muted) {
        await mediaStream.muteAudio();
        return;
      }

      await mediaStream.unmuteAudio();
    } catch (error) {
      zoomProviderLogger.error('Error in the mutting unmutting: ', error);
    }
  }, [muted, mediaStream, currentRoom]);

  const startVideoSharing = useCallback(
    async (videoRef?: MutableRefObject<HTMLVideoElement>) => {
      if (!mediaStream) {
        return;
      }
      const startVideoOptions: CaptureVideoOption = {
        videoElement: videoRef?.current,
        cameraId: activeCameraDeviceId,
      };

      if (mediaStream?.isSupportVirtualBackground()) {
        Object.assign(startVideoOptions, {
          virtualBackground: { imageUrl: 'blur' },
        });
      }

      try {
        await mediaStream.startVideo(startVideoOptions);
        setIsCameraOn(true);
      } catch (error: unknown) {
        setIsCameraOn(false);
        handleZoomError(error);
      }
    },
    [isCameraOn, mediaStream, activeCameraDeviceId],
  );

  const requestPermission = async (name: any) => {
    zoomProviderLogger.log(`requestPermission called for: ${name}`);
    try {
      if (name === 'microphone') {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } else if (name === 'camera') {
        await navigator.mediaDevices.getUserMedia({ video: true });
      }
    } catch (error) {
      zoomProviderLogger.error(`[DevicePermissions]: ${name}:`, error);
    }
  };

  const checkPermissions = async () => {
    zoomProviderLogger.log('checkPermissions called');
    const permissions = ['microphone', 'camera'];

    for (const name of permissions) {
      //@ts-ignore
      const permission = await navigator.permissions.query({ name });

      zoomProviderLogger.log(
        `[DevicePermissions]: ${name} permission: ${permission.state}`,
      );

      if (permission.state !== 'granted') {
        await requestPermission(name);
      } else {
        const microphonePermissionState = await checkSinglePermission(
          'microphone',
        );
        const cameraPermissionState = await checkSinglePermission('camera');

        setPermissionsState({
          microphone: microphonePermissionState,
          camera: cameraPermissionState,
        });
      }
    }
  };

  const checkSinglePermission = async (
    name: PermissionName,
  ): Promise<'granted' | 'denied' | 'prompt'> => {
    const permission =
      await NavigatorPermissionsService.checkAndEnsurePermission(name);
    return permission.state as 'granted' | 'denied' | 'prompt';
  };

  const handleToggleVideoSharing = useCallback(
    async (videoRef?: MutableRefObject<HTMLVideoElement>) => {
      if (!mediaStream) {
        return;
      }

      if (isCameraOn) {
        await mediaStream.stopVideo();
        setIsCameraOn(false);
        return;
      }

      await startVideoSharing(videoRef);
    },
    [isCameraOn, mediaStream, startVideoSharing],
  );

  const leaveZoomSession = useCallback(async () => {
    if (!zmClient || !mediaStream || !zmClient.getSessionInfo()?.isInMeeting) {
      return;
    }

    zoomProviderLogger.log('Leaving session...');
    try {
      if (isSharingScreenToUE) {
        await stopScreenSharing();
      }

      if (isSharingToScreen) {
        await stopShareCameraToScreen();
      }

      if (isCameraOn) {
        await mediaStream.stopVideo();
        setIsCameraOn(false);
      }

      setMuted(true);
      await zmClient.leave();

      setIsInConference(false);
      setZoomConferenceName('');
      setIsJoiningConference(false);
      internalIsJoiningConference.current = false;

      zoomProviderLogger.log('Successfully left the session.');
    } catch (error) {
      zoomProviderLogger.log(`Error leaving session: ${error}`);
    }
  }, [
    zmClient,
    mediaStream,
    isSharingScreenToUE,
    isSharingToScreen,
    isCameraOn,
    stopScreenSharing,
    stopShareCameraToScreen,
  ]);

  return (
    <ZoomContext.Provider
      value={{
        loading,
        // activeMicrophone,
        // activeSpeaker,
        // activeCamera,
        micList,
        speakerList,
        cameraList,
        isMicrophonePermissionGranted:
          permissionsState.microphone === 'granted',
        isCameraPermissionGranted: permissionsState.camera === 'granted',
        handleToggleVideoSharing,
        isCameraOn,
        zoomToken,
        shareViewRef,
        zoomConferenceName,
        zoomUser,
        zoomUE5Token,
        startShareCameraToScreen,
        zmClient,
        stopShareCameraToScreen,
        mediaStream,
        stopScreenSharing,
        isSharingToScreen,
        startScreenSharing,
        isSharingScreenToUE,
        isJoiningConference,
        isInConference,
        handleJoinToConference,
        isCurrentParticipantSpeaking,
        isLimitedScreenResolution,
        setIsLimitedScreenResolution,
        setIsCameraOn,
        expandedScreenSharing,
        setExpandedScreenSharing,
        expandedCameraSharing,
        setExpandedCameraSharing,
        leaveZoomSession,
      }}
    >
      {children}
    </ZoomContext.Provider>
  );
};
